/**
 * Dialog to preview a single AI-generated question (before save).
 * Displays question text, options, correct answer, and detail answer.
 * Math editor content is rendered in-place via MathRenderer.
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Stack,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import MathRenderer from '../common/MathRenderer';
import { QuestionImage } from '../common/QuestionImage';
import { getQuestionTypeLabel } from './AIGeneratedQuestionCard';

/**
 * Normalize options to an array of strings for display.
 * Handles AI/API returning object (e.g. {0: "...", 1: "..."}) or array, or array of dicts.
 */
function normalizeOptions(options) {
  if (options == null) return [];
  if (Array.isArray(options)) {
    return options.map((opt) => {
      if (opt == null) return '';
      if (typeof opt === 'string') return opt;
      if (typeof opt === 'object' && opt !== null && !Array.isArray(opt)) {
        const v = opt.text ?? opt.label ?? opt.value ?? opt.option_text ?? opt.option ?? opt.name;
        return v != null ? String(v) : '';
      }
      return String(opt);
    }).filter(Boolean);
  }
  if (typeof options === 'object' && options !== null) {
    const keys = Object.keys(options);
    const numeric = keys.every((k) => /^\d+$/.test(k));
    const ordered = numeric ? keys.sort((a, b) => Number(a) - Number(b)) : keys;
    return ordered.map((k) => {
      const v = options[k];
      return v != null ? String(v) : '';
    }).filter(Boolean);
  }
  return [];
}

export default function AIPreviewQuestionDialog({ open, onClose, question }) {
  if (!question) return null;

  const options = normalizeOptions(question.options);
  const isMultipleChoice = (question.question_type || '').toUpperCase() === 'MULTIPLE';
  const correctAnswer = question.correct_answer != null ? String(question.correct_answer).trim() : '';
  const correctIndex = isMultipleChoice && options.length > 0 && /^[A-D]$/i.test(correctAnswer)
    ? correctAnswer.toUpperCase().charCodeAt(0) - 65
    : -1;
  const correctOptionText = correctIndex >= 0 && correctIndex < options.length ? options[correctIndex] : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { m: { xs: 1, sm: 2 }, width: { xs: 'calc(100% - 16px)', sm: undefined } } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Preview question</Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, overflowX: 'hidden' }}>
        <Stack spacing={3}>
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 0 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'primary.main' }}>
              Question details
            </Typography>
            <Stack spacing={2}>
              {question.topic_name && (
                <>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Topic
                    </Typography>
                    <Typography variant="body1">{question.topic_name}</Typography>
                  </Box>
                  <Divider />
                </>
              )}
              {question.question_type && (
                <>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Type
                    </Typography>
                    <Typography variant="body1">{getQuestionTypeLabel(question.question_type)}</Typography>
                  </Box>
                  <Divider />
                </>
              )}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 0.5 }}>
                  Question text
                </Typography>
                <Box sx={{ mt: 1, p: { xs: 1.25, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1, overflowWrap: 'anywhere' }}>
                  <MathRenderer content={question.question_text} variant="body1" />
                </Box>
              </Box>
              {(question.image_proxy_url || question.image_url) && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 0.5 }}>
                      Visual
                    </Typography>
                    <QuestionImage question={question} alt="Generated visual" />
                  </Box>
                </>
              )}
              {options.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 0.5 }}>
                      Options
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      {options.map((opt, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: { xs: 1.25, sm: 2 },
                            py: 1,
                            borderRadius: 1,
                            bgcolor: 'grey.50',
                            border: '1px solid',
                            borderColor: 'divider',
                            minWidth: 0,
                          }}
                        >
                          <Typography variant="body2" sx={{ mr: 2, fontWeight: 'bold', minWidth: '30px' }}>
                            {String.fromCharCode(65 + idx)}.
                          </Typography>
                          <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                            <MathRenderer content={opt} variant="body2" />
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 0 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 0.5 }}>
              Correct answer
            </Typography>
            <Box sx={{ mt: 1, p: { xs: 1.25, sm: 2 }, bgcolor: 'success.light', borderRadius: 1, overflowWrap: 'anywhere' }}>
              {correctOptionText ? (
                <>
                  <Typography component="span" variant="body1" sx={{ fontWeight: 600, mr: 1 }}>
                    {correctAnswer}.
                  </Typography>
                  <MathRenderer content={correctOptionText} variant="body1" />
                </>
              ) : (
                <MathRenderer content={question.correct_answer || '—'} variant="body1" />
              )}
            </Box>
          </Paper>

          {question.detail_answer && String(question.detail_answer).trim() && (
            <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 0 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 0.5 }}>
                Detailed solution
              </Typography>
              <Box sx={{ mt: 1, p: { xs: 1.25, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1, overflowWrap: 'anywhere' }}>
                <MathRenderer content={question.detail_answer} variant="body1" />
              </Box>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
